/* eslint-disable css-modules/no-unused-class, no-nested-ternary  */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { coreService } from 'network-utils';
import Xarrow from 'react-xarrows';
import classNames from 'classnames';
import { translate } from 'translations';
import { Avatar, Icon, Tooltip } from 'learning-panel-components';
import ProgramLanguageSelectionContainer from '@components/common/programLanguageSelection/programLanguageSelectionContainer';
import SkillTreeOverview from '@components/skillTree/overview/overview';
import MapLegend from '@components/skillTree/mapLegend/mapLegend';
import CertificatePopupContainer from '@components/home/certificates/certificatePopupContainer';
import { languagesNameToDisplayName } from 'utils';
import { useSelector } from 'react-redux';
import { selectUserNickname, selectUserFullName } from '@redux/slices/userSlice';
import { LearningPathNodeProps, LearningPathProps } from './learningPathInterfaces';
import LearningPathMap from './mapOverview/learningPathMap';
import ContentItem, { ContentItemStatus } from './contentItems/contentItem';
import Inventory from './Inventory/inventory';
import { getNodePoints } from './utils';
import styles from './learningPath.module.scss';
import { useNotifyScrollActivity } from '../../hooks/useNotifyScrollActivity';

const LearningPath: FC<LearningPathProps> = (props: LearningPathProps) => {
    const [nodeIdsToNodesMap, setNodeIdsToNodesMap] = useState<{ [key: number]: LearningPathNodeProps }>({});
    const [levelToNodeIdsMap, setLevelToNodeIdsMap] = useState<{ [key: number]: LearningPathNodeProps[] }>({});
    const [areNodesRendered, setAreNodesRendered] = useState<boolean>(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [hoveredType, setHoveredType] = useState<string | null>(null);
    const [hoveredElements, setHoveredElements] = useState<{ type: string; id: string }[]>([]);
    const [currentLevel, setCurrentLevel] = useState<number>(-1);
    const [showCertificate, setShowCertificate] = useState<boolean>(false);
    const [scrollPercent, setScrollPercent] = useState<number>(0);
    const [inventoryId, setInventoryId] = useState<string>('');
    const [showProgramLanguageSelection, setShowProgramLanguageSelection] = useState<boolean>(false);

    const inventoryRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const containerRef = useRef<any>(null);
    const currentLevelRef = useRef<any>(null);
    const prereqCache = useRef(new Map<number, boolean>());
    const notifyScrollActivity = useNotifyScrollActivity();
    const navigate = useNavigate();
    const showUserAvatarInRoot = props.status === 'not_started';
    const nickname = useSelector(selectUserNickname);
    const fullName = useSelector(selectUserFullName);

    useEffect(() => {
        let currLevel = 0;
        const levels = Object.keys(levelToNodeIdsMap).map(level => parseInt(level, 10));
        if (levels.length === 0) return;
        const maxLevel = Math.max(...levels);

        for (let level = 0; level <= maxLevel; level++) {
            const nodes = levelToNodeIdsMap[level];
            if (nodes.every(node => node.status === null)) {
                break;
            }
            currLevel = level;
        }

        setCurrentLevel(currLevel);
    }, [levelToNodeIdsMap]);

    useEffect(() => {
        if (props.languages.length === 0) setShowProgramLanguageSelection(true);
    }, [props.languages]);

    useEffect(() => {
        prereqCache.current.clear();
        const nodeIdsToNodesMapTmp: { [key: number]: LearningPathNodeProps } = {};
        props.nodes.forEach(node => {
            nodeIdsToNodesMapTmp[node.id] = node;
        });
        setNodeIdsToNodesMap(nodeIdsToNodesMapTmp);

        const levelToNodeIdsTmpMap: { [key: number]: LearningPathNodeProps[] } = {};
        const nodeIdsToLevelMap: { [key: number]: number } = {};

        const calcLevel = (node: LearningPathNodeProps, level: number) => {
            if (nodeIdsToLevelMap[node.id] !== undefined) {
                const prevLevel = nodeIdsToLevelMap[node.id];
                if (prevLevel >= level) {
                    return;
                }

                levelToNodeIdsTmpMap[prevLevel] = levelToNodeIdsTmpMap[prevLevel].filter(n => n.id !== node.id);
            }

            nodeIdsToLevelMap[node.id] = level;
            if (levelToNodeIdsTmpMap[level] === undefined) {
                levelToNodeIdsTmpMap[level] = [];
            }
            levelToNodeIdsTmpMap[level].push(node);

            node.children.forEach(childId => {
                calcLevel(nodeIdsToNodesMapTmp[childId], level + 1);
            });
        };

        const root = props.nodes.find(node => node.parents.length === 0);
        if (!root) return;
        calcLevel(root, 0);

        setLevelToNodeIdsMap(levelToNodeIdsTmpMap);
    }, [props.nodes]);

    useEffect(() => {
        if (!areNodesRendered && containerRef?.current?.clientHeight > 0) {
            setAreNodesRendered(true);
        }
    }, [containerRef?.current?.clientHeight, areNodesRendered]);

    useEffect(() => {
        if (currentLevelRef.current && areNodesRendered && currentLevel !== -1 && currentLevel !== 0) {
            const containerClientRect = containerRef.current.getBoundingClientRect();
            const currLevelClientRect = currentLevelRef.current.getBoundingClientRect();
            const diff = currLevelClientRect.bottom - containerClientRect.bottom;
            const containerHeight = containerClientRect.bottom - containerClientRect.top;
            const levelHeight = currLevelClientRect.bottom - currLevelClientRect.top;
            const centerScroll = diff + containerHeight / 2 - levelHeight / 2;
            if (centerScroll < 0) {
                containerRef.current.scroll({ top: centerScroll, behavior: 'smooth' });
            }
        }
    }, [areNodesRendered, currentLevelRef, currentLevel]);

    useEffect(() => {
        const checkIfClickedOutside = (e: any) => {
            if (inventoryId !== '' && inventoryRef.current && !inventoryRef.current.contains(e.target)) {
                setInventoryId('');
            }
        };
        document.addEventListener('mousedown', checkIfClickedOutside);
        return () => {
            document.removeEventListener('mousedown', checkIfClickedOutside);
        };
    }, [inventoryId]);

    const handleMouseEnter = (id: string, type: string) => {
        setHoveredId(id);
        setHoveredType(type);
    };

    const handleMouseLeave = () => {
        setHoveredId(null);
        setHoveredType(null);
    };

    const setBackNavigation = () => {
        if (!document.referrer || document.referrer.includes('home')) navigate('/app/home');
        else navigate('/app/learn');
    };

    const getHoveredElementsForNode = useCallback(
        (node: LearningPathNodeProps, hoveredId: string): { type: string; id: string }[] => {
            const result = [{ type: 'node', id: hoveredId }];

            if (node.status === coreService.ProgressStatus.COMPLETED) {
                node.children.forEach(childId => {
                    const childNode = nodeIdsToNodesMap[childId];
                    if (childNode.status === null) {
                        result.push({ type: 'arrow', id: `arrow${node.id}-${childId}` });
                        result.push({ type: 'node', id: `node${childId}` });
                    }
                });
            } else if (node.status === null) {
                node.parents.forEach(parentId => {
                    const parentNode = nodeIdsToNodesMap[parentId];
                    if (parentNode.status === coreService.ProgressStatus.COMPLETED) {
                        result.push({ type: 'arrow', id: `arrow${parentId}-${node.id}` });
                    }
                });
            }

            return result;
        },
        [nodeIdsToNodesMap]
    );

    const getHoveredElementsForArrow = useCallback(
        (arrowId: string): { type: string; id: string }[] => {
            const [nodeId1, nodeId2] = arrowId.replace('arrow', '').split('-');
            const node1 = nodeIdsToNodesMap[parseInt(nodeId1, 10)];
            const node2 = nodeIdsToNodesMap[parseInt(nodeId2, 10)];

            if (node1.status === coreService.ProgressStatus.COMPLETED && node2.status === null) {
                const result = [
                    { type: 'arrow', id: arrowId },
                    { type: 'node', id: `node${nodeId2}` }
                ];
                return result;
            }

            return [];
        },
        [nodeIdsToNodesMap]
    );

    const getHoveredElements = useCallback((): { type: string; id: string }[] => {
        if (!hoveredId || !hoveredType) return [];

        if (hoveredType === 'node') {
            const node = nodeIdsToNodesMap[parseInt(hoveredId.replace('node', ''), 10)];
            return getHoveredElementsForNode(node, hoveredId);
        }

        if (hoveredType === 'arrow') {
            return getHoveredElementsForArrow(hoveredId);
        }

        return [];
    }, [hoveredId, hoveredType, nodeIdsToNodesMap, getHoveredElementsForArrow, getHoveredElementsForNode]);

    useEffect(() => {
        setHoveredElements(getHoveredElements());
    }, [hoveredId, hoveredType, getHoveredElements]);

    const overviewLanguages = useMemo(
        () => props.languages.map(lang => languagesNameToDisplayName[lang]),
        [props.languages]
    );

    const onEditLanguages = useCallback(() => setShowProgramLanguageSelection(true), []);
    const onShowCertificate = useCallback(() => setShowCertificate(true), []);

    const nodeSelectHandlers = useMemo(() => {
        const handlers: { [id: number]: () => void } = {};
        Object.values(nodeIdsToNodesMap).forEach((node: LearningPathNodeProps) => {
            handlers[node.id] = () => props.onSelectNode(node);
        });
        return handlers;
    }, [nodeIdsToNodesMap, props.onSelectNode]);

    const handleScroll = (event: any) => {
        notifyScrollActivity(containerRef.current);

        const maxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
        if (maxScroll === 0) {
            setScrollPercent(0);
            return;
        }

        setScrollPercent(-(event.currentTarget.scrollTop / maxScroll) * 100);
    };

    const arePrerequisitesMet = (node: LearningPathNodeProps): boolean => {
        if (prereqCache.current.has(node.id)) return prereqCache.current.get(node.id)!;
        if (node.parents.length === 0) {
            prereqCache.current.set(node.id, true);
            return true;
        }
        prereqCache.current.set(node.id, false); // cycle guard sentinel
        const mandatoryParentIds: number[] = [];
        const optionalParentIds: number[] = [];
        node.parents.forEach(parentId => {
            const parent = nodeIdsToNodesMap[parentId];
            if (!parent) return;
            if (parent.isMandatory === true) mandatoryParentIds.push(parentId);
            else optionalParentIds.push(parentId);
        });
        const allMandatoryCompleted = mandatoryParentIds.every(
            parentId => nodeIdsToNodesMap[parentId].status === coreService.ProgressStatus.COMPLETED
        );
        if (!allMandatoryCompleted) {
            return false;
        }
        const result = optionalParentIds.every(parentId => arePrerequisitesMet(nodeIdsToNodesMap[parentId]));
        prereqCache.current.set(node.id, result);
        return result;
    };

    const calcNodeStatus = (node: LearningPathNodeProps): ContentItemStatus => {
        if (node.status !== null) {
            return node.status;
        }
        return arePrerequisitesMet(node) ? 'takable' : 'notTakable';
    };

    return (
        <div className={styles['learning-path-wrapper']}>
            {showProgramLanguageSelection && (
                <div className={styles['program-langues-wrapper']}>
                    <ProgramLanguageSelectionContainer onClose={() => setShowProgramLanguageSelection(false)} />
                </div>
            )}
            <div className={styles.back}>
                <div className={styles['back-content']} onClick={(): void => setBackNavigation()}>
                    <Icon name="back" className={styles['back-icon']} />
                    <div>{translate('Back')}</div>
                </div>
            </div>
            <div className={styles['learning-path-overview-wrapper']}>
                <SkillTreeOverview
                    title={translate(props.name)}
                    score={props.score}
                    progress={props.progress || 0}
                    duration={props.durationMinutes}
                    languages={overviewLanguages}
                    isSkillTree={false}
                    onEditLanguages={onEditLanguages}
                    isDiplomaEnabled={props.status === 'completed'}
                    onShowCertificate={onShowCertificate}
                    badgeName={props.badge}
                />
            </div>
            <div className={styles['learning-path-map-legend-wrapper']}>
                <MapLegend />
            </div>
            <div ref={containerRef} className={styles['learning-path-container']} onScroll={handleScroll}>
                <div className={styles['start-img-wrapper']}>
                    <img
                        alt="startImage"
                        className={styles['start-img']}
                        src={`${process.env.PUBLIC_URL}/images/startPath.svg`}
                    />
                    {showUserAvatarInRoot && (
                        <div className={styles['user-avatar-root']}>
                            <Avatar name={props.nickname} avatarSrc={props.profilePicture} />
                        </div>
                    )}
                </div>
                {Object.keys(levelToNodeIdsMap).map(level => {
                    const nodes = levelToNodeIdsMap[parseInt(level, 10)];
                    const isCurrentLevel = parseInt(level, 10) === currentLevel;

                    return (
                        <div
                            key={level}
                            style={{ display: 'flex', flexDirection: 'row' }}
                            ref={isCurrentLevel ? currentLevelRef : null}
                        >
                            {nodes.map(node => {
                                const isHovered = hoveredElements.some(element => element.id === `node${node.id}`);

                                return (
                                    <div
                                        key={node.id}
                                        id={`node${node.id}`}
                                        className={styles['learning-path-node']}
                                        onMouseEnter={() => handleMouseEnter(`node${node.id}`, 'node')}
                                        onMouseLeave={handleMouseLeave}
                                        style={{ position: 'relative' }}
                                    >
                                        <ContentItem
                                            progress={node.progressPercentage}
                                            status={calcNodeStatus(node)}
                                            type={node.type}
                                            userNickname={props.nickname}
                                            userAvatar={props.profilePicture}
                                            duration={node.durationMinutes}
                                            name={node.name}
                                            points={getNodePoints(node)}
                                            isHovered={isHovered}
                                            outerLineTo="left"
                                            onSelectNode={nodeSelectHandlers[node.id]}
                                            numberOfItems={node.numberOfItems}
                                            id={`node${node.id}`}
                                            setInventoryId={setInventoryId}
                                            isOptional={!node.isMandatory}
                                            showUserAvatar={
                                                !showUserAvatarInRoot && props.lastSelectedNodeId === node.id
                                            }
                                            isRoot={node.parents.length === 0}
                                        />
                                        {inventoryId === `node${node.id}` && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    zIndex: 999,
                                                    left: '6rem',
                                                    top: '2rem'
                                                }}
                                                ref={inventoryRef}
                                            >
                                                <Inventory
                                                    name={node.name}
                                                    inventoryItems={node.itemTypesCount}
                                                    maxScore={node.potentialScore}
                                                    score={node.score}
                                                    onClose={() => setInventoryId('')}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}

                {areNodesRendered &&
                    props.nodes.map(node => {
                        return node.children.map(childId => {
                            const childNode = nodeIdsToNodesMap[childId];
                            const isActive =
                                node.status === coreService.ProgressStatus.COMPLETED && childNode.status !== null;
                            const isHovered = hoveredElements.some(
                                element => element.id === `arrow${node.id}-${childId}`
                            );
                            return (
                                <div
                                    id={`arrow${node.id}-${childId}`}
                                    key={`${node.id}-${childId}-wrapper`}
                                    className={classNames({
                                        [styles['active-arrow']]: isActive,
                                        [styles['hovered-arrow']]: isHovered
                                    })}
                                    onMouseEnter={() => handleMouseEnter(`arrow${node.id}-${childId}`, 'arrow')}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <Xarrow
                                        startAnchor="top"
                                        endAnchor="bottom"
                                        key={`${node.id}-${childId}`}
                                        start={`node${node.id}`}
                                        end={`node${childId}`}
                                        strokeWidth={1}
                                        color={'#192F6E'}
                                    />
                                </div>
                            );
                        });
                    })}
                <div className={styles['flags-background']} />
                <div className={styles['start-background']}>
                    <Tooltip
                        key="learning-path-name-tooltip"
                        placement="bottom"
                        title={<div>{translate(props.name)}</div>}
                        enterDelay
                    >
                        <div className={styles.name}>{translate(props.name)}</div>
                    </Tooltip>
                    <div className={styles.level}>{translate(props.level)}</div>
                </div>
                <div className={styles['certificate-wrapper']}>
                    <img
                        alt="certificateStatus"
                        className={
                            styles[
                                props.badge
                                    ? 'badge-image'
                                    : props.status === 'completed'
                                    ? 'certificate-img'
                                    : 'certificate-img-disabled'
                            ]
                        }
                        src={
                            props.badge
                                ? `${process.env.PUBLIC_URL}/images/learningPathBadges/${`${props.badge}End`}.svg`
                                : `${process.env.PUBLIC_URL}/images/learningPathCertificateDetails.webp`
                        }
                        onClick={() => (props.status === 'completed' ? setShowCertificate(true) : undefined)}
                    />
                </div>
            </div>
            <div className={styles['learning-path-map-wrapper']}>
                <LearningPathMap
                    nodes={props.nodes}
                    levelToNodeIdsMap={levelToNodeIdsMap}
                    scrollPercent={scrollPercent}
                    level={currentLevel}
                    badgeName={props.badge}
                />
            </div>

            {showCertificate && (
                <CertificatePopupContainer
                    onClose={() => setShowCertificate(false)}
                    inputCertificateData={{
                        entityDisplayName: props.name,
                        name: fullName || nickname,
                        completedAt: props.completedAt || ''
                    }}
                    isSkillTree={false}
                    isLearningPath={true}
                    isCustom={false}
                />
            )}
        </div>
    );
};

export default LearningPath;
